import type { SessionConfig } from '../types';

/**
 * Per-thread capability tokens (server contract: response-one-prod
 * lib/widget/threadCapability.ts).
 *
 * The widget token only proves org identity and is shared by every visitor
 * of the org, so the server returns a signed `threadToken` bound to
 * {orgId, threadId} with chat and thread-create responses. Any client that
 * later reads or mutates that specific thread must send the token back
 * (header `x-widget-thread-token`), or those calls will be rejected once
 * WIDGET_THREAD_CAP_ENFORCE is enabled server-side.
 *
 * The storage key prefix matches the v1 loader exactly so visitors keep
 * their thread capability when a site moves between the v1 loader and this
 * bundle. Storage failures fall back to an in-memory map — persistence is
 * best-effort and must never break the chat flow.
 */

const THREAD_TOKEN_KEY_PREFIX = 'rcx_widget_thread_token:';

const memoryTokens = new Map<string, string>();

function getStorage(config?: SessionConfig): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return config?.storage === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

export function storeThreadToken(
  threadId: string | null | undefined,
  token: unknown,
  config?: SessionConfig
): void {
  if (!threadId || typeof token !== 'string' || !token) return;
  const key = THREAD_TOKEN_KEY_PREFIX + threadId;
  const storage = getStorage(config);
  if (storage) {
    try {
      storage.setItem(key, token);
      return;
    } catch {
      // Storage unavailable (private mode, quota) — fall through to the
      // memory map so the token survives for this page view; the server
      // stays in grace mode for later visits.
    }
  }
  memoryTokens.set(key, token);
}

export function getThreadToken(
  threadId: string | null | undefined,
  config?: SessionConfig
): string {
  if (!threadId) return '';
  const key = THREAD_TOKEN_KEY_PREFIX + threadId;
  const storage = getStorage(config);
  if (storage) {
    try {
      // Reachable storage is the single source of truth; the memory map is
      // only consulted when storage cannot be read at all.
      return storage.getItem(key) || '';
    } catch {
      // fall through to memory
    }
  }
  return memoryTokens.get(key) || '';
}

export function clearThreadToken(
  threadId: string | null | undefined,
  config?: SessionConfig
): void {
  if (!threadId) return;
  const key = THREAD_TOKEN_KEY_PREFIX + threadId;
  memoryTokens.delete(key);
  try {
    getStorage(config)?.removeItem(key);
  } catch {
    // ignore
  }
}

/** Header fragment to spread into per-thread request headers. */
export function threadCapabilityHeaders(
  threadId: string | null | undefined,
  config?: SessionConfig
): Record<string, string> {
  const token = getThreadToken(threadId, config);
  return token ? { 'x-widget-thread-token': token } : {};
}
